'use client'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import TrashIcon2 from '@/icons/TrashIcon2'
import { PAGE_PATH } from '@/lib/page-paths'
import { cn } from '@/lib/utils'
import { Bookmark, BookOpen, Copy, Heart, MoreVertical, Pencil, Tag } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'
import { toast } from 'react-toastify'

type AppTemplateMenuProps = {
  id?: string
  onDeleteAction?: () => void
  onDuplicateAction?: () => void
  onEditTags?: (event: React.MouseEvent) => void
  onRenameAction?: () => void
  isBookmarked?: boolean
  onBookmarkToggle?: () => Promise<void> | void
  bookmarkVariant?: 'bookmark' | 'favorite'
  copyLinkPath?: string
}

export default function AppTemplateMenu({
  id,
  onDeleteAction,
  onDuplicateAction,
  onEditTags,
  onRenameAction,
  isBookmarked = false,
  onBookmarkToggle,
  bookmarkVariant = 'bookmark',
  copyLinkPath,
}: AppTemplateMenuProps) {
  const router = useRouter()

  const handleCopyLinkClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!id) {
        toast.error('No ID available for navigation')
        return
      }
      const linkPath = copyLinkPath || `/copilot/${id}`
      navigator.clipboard.writeText(`${window.location.origin}${linkPath}`)
      toast.success('Link copied to clipboard')
    },
    [id, copyLinkPath]
  )

  const handleProjectClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (id) {
        router.push(`${PAGE_PATH.COPILOT}/${id}`)
      } else {
        toast.error('No ID available for navigation')
      }
    },
    [id, router]
  )

  const handleDuplicateClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDuplicateAction?.()
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDeleteAction?.()
  }

  const handleEditTagsClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEditTags?.(e)
  }

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRenameAction?.()
  }

  const handleBookmarkToggle = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await onBookmarkToggle?.()
  }

  const baseMenuItemClass =
    'flex items-center gap-x-2 rounded-xl px-3 py-2 font-medium text-base transition'
  const defaultMenuItemClass = cn(baseMenuItemClass, 'text-gray-900 hover:bg-gray-100')
  const deleteMenuItemClass = cn(baseMenuItemClass, 'text-red-600 hover:bg-gray-100')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex h-[22px] w-[22px] items-center justify-center rounded-full transition-colors duration-200 hover:bg-black/10"
          data-tooltip-id="toolbar-tooltip"
          data-tooltip-content="More options"
          onClick={(e) => e.preventDefault()}
        >
          <MoreVertical className="h-4 w-4 text-gray-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="left"
        align="start"
        className="w-[240px] rounded-2xl border border-gray-200 bg-white p-2 shadow-xl"
      >
        {onRenameAction && (
          <DropdownMenuItem className={defaultMenuItemClass} onClick={handleRenameClick}>
            <Pencil className="h-5 w-5" />
            Rename
          </DropdownMenuItem>
        )}
        {onBookmarkToggle && (
          <DropdownMenuItem className={defaultMenuItemClass} onClick={handleBookmarkToggle}>
            {bookmarkVariant === 'favorite' ? (
              <>
                <Heart className={cn('h-5 w-5', isBookmarked && 'fill-current text-red-500')} />
                {isBookmarked ? 'Unfavorite' : 'Favorite'}
              </>
            ) : (
              <>
                <Bookmark className={cn('h-5 w-5', isBookmarked && 'fill-current text-blue-600')} />
                {isBookmarked ? 'Remove Bookmark' : 'Bookmark'}
              </>
            )}
          </DropdownMenuItem>
        )}
        {(onRenameAction || onBookmarkToggle) && (id || onDuplicateAction || onEditTags) && (
          <DropdownMenuSeparator className="my-2 bg-gray-200" />
        )}
        {id && (
          <DropdownMenuItem className={defaultMenuItemClass} onClick={handleProjectClick}>
            <BookOpen className="h-5 w-5" />
            Open Project
          </DropdownMenuItem>
        )}
        {id && (
          <DropdownMenuItem className={defaultMenuItemClass} onClick={handleCopyLinkClick}>
            <Copy className="h-5 w-5" />
            Copy Link
          </DropdownMenuItem>
        )}
        {onDuplicateAction && (
          <DropdownMenuItem className={defaultMenuItemClass} onClick={handleDuplicateClick}>
            <Copy className="h-5 w-5" />
            Duplicate
          </DropdownMenuItem>
        )}
        {onEditTags && (
          <DropdownMenuItem className={defaultMenuItemClass} onClick={handleEditTagsClick}>
            <Tag className="h-5 w-5" />
            Edit Tags
          </DropdownMenuItem>
        )}
        {onDeleteAction && id && <DropdownMenuSeparator className="my-2 bg-gray-200" />}
        {onDeleteAction && (
          <DropdownMenuItem className={deleteMenuItemClass} onClick={handleDeleteClick}>
            <TrashIcon2 className="h-5 w-5 text-red-600" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
